/*
  Happy Nuts analytics bridge.

  Pushes GA4-ready events into GTM's dataLayer for custom cart,
  upsell, and bundle flows. Standard Shopify/Google app events should
  keep owning normal checkout and purchase tracking.
*/
(function () {
  'use strict';

  if (window.HnAnalytics) return;

  /* Off for the kill switch, or when the visitor declined analytics or sent
     Global Privacy Control (snippets/hn-consent-core). */
  function analyticsDisabled() {
    var consent = window.HnConsent;
    return window.HN_TRACKING_DISABLED === true
      || window.HnTrackingDisabled === true
      || (!!consent && !consent.analytics());
  }

  var SESSION_PREFIX = 'hn_analytics:';
  var nextDrawerOpenReason = '';

  function currencyCode() {
    if (window.Shopify && window.Shopify.currency) {
      return window.Shopify.currency.active || window.Shopify.currency.currency || 'USD';
    }
    return 'USD';
  }

  function toDollars(cents) {
    var value = parseInt(cents, 10);
    if (isNaN(value)) value = 0;
    return Math.round(value) / 100;
  }

  function emptyCheckoutFlags() {
    return {
      had_upsell_view: false,
      had_upsell_click: false,
      had_upsell_add: false,
      had_pdp_bundle_add: false,
      had_pdp_final_cta_add: false
    };
  }

  if (analyticsDisabled()) {
    window.HnAnalytics = {
      trackingDisabled: analyticsDisabled,
      currencyCode: currencyCode,
      toDollars: toDollars,
      pushEvent: function () {},
      pushEcommerce: function () {},
      markSessionFlag: function () {},
      getCheckoutFlags: emptyCheckoutFlags,
      setNextCartDrawerOpenReason: function () {},
      trackCartDrawerView: function () {}
    };
    return;
  }

  function safeSessionGet(key) {
    try {
      return window.sessionStorage ? window.sessionStorage.getItem(SESSION_PREFIX + key) : null;
    } catch (e) {
      return null;
    }
  }

  function safeSessionSet(key, value) {
    try {
      if (window.sessionStorage) window.sessionStorage.setItem(SESSION_PREFIX + key, value || '1');
    } catch (e) {}
  }

  function sessionFlag(name) {
    return safeSessionGet(name) === '1';
  }

  function markSessionFlag(name) {
    safeSessionSet(name, '1');
  }

  function parseMoneyTextToCents(text) {
    if (!text) return 0;
    var cleaned = String(text).replace(/[^0-9.,-]/g, '').replace(/,/g, '');
    var parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : Math.round(parsed * 100);
  }

  function assign(target, source) {
    target = target || {};
    source = source || {};
    Object.keys(source).forEach(function (key) {
      target[key] = source[key];
    });
    return target;
  }

  function markFromEvent(eventName, params) {
    params = params || {};
    var isCustomAdd = eventName === 'hn_add_to_cart' || eventName === 'add_to_cart';
    if (params.cart_source === 'cart_upsell') {
      if (eventName === 'view_promotion') markSessionFlag('had_upsell_view');
      if (eventName === 'select_promotion') markSessionFlag('had_upsell_click');
      if (isCustomAdd) markSessionFlag('had_upsell_add');
    }
    if (isCustomAdd && params.cart_source === 'pdp_bundle') {
      markSessionFlag('had_pdp_bundle_add');
    }
    if (isCustomAdd && params.cart_source === 'pdp_final_cta') {
      markSessionFlag('had_pdp_final_cta_add');
    }
  }

  function pushEvent(eventName, payload, options) {
    if (!eventName) return;
    payload = payload || {};
    options = options || {};

    window.dataLayer = window.dataLayer || [];
    if (options.ecommerce) {
      window.dataLayer.push({ ecommerce: null });
    }

    window.dataLayer.push(assign({ event: eventName }, payload));
    markFromEvent(eventName, payload);

    /* First-party telemetry mirror (hn-telemetry.js). Runs AFTER the
       dataLayer push with the same payload, so GA4 receives exactly
       what it always did. options.telemetryParams is the Supabase-only
       side channel: extra fields the mirror merges into its copy that
       NEVER touch the dataLayer. No-ops when telemetry is disabled. */
    if (window.HnTelemetry && typeof window.HnTelemetry.mirror === 'function') {
      try {
        window.HnTelemetry.mirror(eventName, payload, options.telemetryParams);
      } catch (e) {}
    }
  }

  function pushEcommerce(eventName, ecommerce, params, options) {
    params = params || {};
    pushEvent(eventName, assign({ ecommerce: ecommerce || {} }, params), assign({ ecommerce: true }, options || {}));
  }

  function getCheckoutFlags() {
    return assign(emptyCheckoutFlags(), {
      had_upsell_view: sessionFlag('had_upsell_view'),
      had_upsell_click: sessionFlag('had_upsell_click'),
      had_upsell_add: sessionFlag('had_upsell_add'),
      had_pdp_bundle_add: sessionFlag('had_pdp_bundle_add'),
      had_pdp_final_cta_add: sessionFlag('had_pdp_final_cta_add')
    });
  }

  function readCartState(surfaceEl) {
    surfaceEl = surfaceEl || document;
    var totalEl =
      surfaceEl.querySelector && surfaceEl.querySelector('[data-hn-cart-checkout-total]') ||
      document.querySelector('[data-hn-cart-checkout-total]') ||
      document.querySelector('.totals__total-value');
    var countEl =
      document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]') ||
      document.querySelector('#cart-icon-bubble .cart-count-bubble span') ||
      document.querySelector('.drawer__heading') ||
      document.querySelector('.hn-cart-page__heading');

    var valueCents = totalEl ? parseMoneyTextToCents(totalEl.textContent) : 0;
    var itemCount = 0;
    if (countEl) {
      var match = (countEl.textContent || '').match(/\d+/);
      itemCount = match ? parseInt(match[0], 10) || 0 : 0;
    }

    return {
      cart_value: toDollars(valueCents),
      cart_value_cents: valueCents,
      item_count: itemCount
    };
  }

  function trackCheckoutClick(button) {
    if (!button) return;
    var surface = button.getAttribute('data-hn-checkout-surface') || 'unknown';
    var valueCents = parseInt(button.getAttribute('data-hn-cart-value-cents'), 10);
    var itemCount = parseInt(button.getAttribute('data-hn-cart-item-count'), 10);
    var freeShipping = button.getAttribute('data-hn-free-shipping-unlocked');
    var state = readCartState(button.closest('cart-drawer, #main-cart-footer') || document);

    if (!isNaN(valueCents)) {
      state.cart_value_cents = valueCents;
      state.cart_value = toDollars(valueCents);
    }
    if (!isNaN(itemCount)) state.item_count = itemCount;

    pushEvent('hn_checkout_click', assign(assign({
      cart_surface: surface,
      free_shipping_unlocked: freeShipping === 'true'
    }, state), getCheckoutFlags()));
  }

  function setNextCartDrawerOpenReason(reason) {
    nextDrawerOpenReason = reason || '';
  }

  function inferDrawerReason(triggeredBy) {
    if (nextDrawerOpenReason) {
      var reason = nextDrawerOpenReason;
      nextDrawerOpenReason = '';
      return reason;
    }
    if (triggeredBy && triggeredBy.id === 'cart-icon-bubble') return 'manual';
    if (triggeredBy && triggeredBy.closest && triggeredBy.closest('[data-hn-pdp-product-form]')) return 'pdp_atc';
    return 'unknown';
  }

  function trackCartDrawerView(options) {
    options = options || {};
    var reason = options.open_reason || inferDrawerReason(options.triggeredBy);
    var dedupeKey = 'cart_drawer_view:' + reason;
    if (safeSessionGet(dedupeKey)) return;
    safeSessionSet(dedupeKey, '1');

    pushEvent('hn_cart_drawer_view', assign({
      cart_surface: 'drawer',
      open_reason: reason
    }, readCartState(document.querySelector('cart-drawer') || document)));
  }

  document.addEventListener('click', function (event) {
    var checkout = event.target.closest('[data-hn-checkout-track]');
    if (checkout) trackCheckoutClick(checkout);
  }, true);

  /* Standard product-form adds (PDP form, collection quick-add) run stock
     Dawn code with no analytics of their own. product-form.js publishes
     cartUpdate with source 'product-form' after every successful
     /cart/add.js POST, so one subscriber covers all of those surfaces.
     The instrumented flows (PDP bundle, cart upsell, ALP buybox) publish
     under their own sources and emit their own events, so filtering on
     source cannot double count. */
  var CART_SOURCE_BY_PROVENANCE = {
    'PDP add to cart': 'pdp_form',
    'Collection quick add': 'collection_quick_add',
    'Product form add to cart': 'product_form'
  };

  function lineToAnalyticsItem(line) {
    var qty = parseInt(line.quantity, 10) || 1;
    var unitCents = typeof line.final_price === 'number'
      ? line.final_price
      : parseInt(line.price, 10) || 0;
    return {
      item_id: String(line.variant_id || line.id || ''),
      item_name: line.product_title || line.title || '',
      item_variant: line.variant_title || '',
      item_product_id: line.product_id ? String(line.product_id) : '',
      item_variant_id: String(line.variant_id || line.id || ''),
      item_sku: line.sku || '',
      item_handle: line.handle || '',
      price: unitCents / 100,
      discount: 0,
      quantity: qty
    };
  }

  function trackProductFormAdd(event) {
    if (!event || event.source !== 'product-form' || !event.cartData) return;
    /* /cart/add.js answers a single form add with the line item object
       itself (variant_id at the top level); items[] covers the multi-line
       response shape defensively. */
    var lines = Array.isArray(event.cartData.items)
      ? event.cartData.items
      : (event.cartData.variant_id !== undefined ? [event.cartData] : []);
    if (!lines.length) return;

    var items = lines.map(lineToAnalyticsItem);
    var valueCents = 0;
    items.forEach(function (item) {
      valueCents += Math.round(item.price * 100) * item.quantity;
    });

    /* product-form.js stamps properties[_hn_source] at add time and the
       cart API echoes it back, so the exact surface rides the response. */
    var provenance = lines[0].properties && lines[0].properties._hn_source;
    var cartSource = CART_SOURCE_BY_PROVENANCE[provenance] || 'product_form';

    pushEcommerce('hn_add_to_cart', {
      currency: currencyCode(),
      value: valueCents / 100,
      items: items
    }, { cart_source: cartSource });
  }

  /* ---- First-party-only journey events ------------------------------
     Browsing signals recorded straight to Supabase via recordEvent; GA4
     already has its own page/search reporting, so none of these mirror
     into the dataLayer. All listeners are delegated so they survive
     section re-renders, and every emitter is fail-open. */
  function recordTelemetry(eventName, params) {
    try {
      if (window.HnTelemetry && typeof window.HnTelemetry.recordEvent === 'function') {
        window.HnTelemetry.recordEvent(eventName, params || {});
      }
    } catch (e) {}
  }

  /* Search: a submitted query (header predictive input or the search
     page both post to /search), and predictive-suggestion picks, which
     navigate without ever submitting the form. */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[action*="/search"]')) return;
    var input = form.querySelector('input[name="q"]');
    var query = input && input.value ? input.value.trim() : '';
    if (!query) return;
    recordTelemetry('hi_search', {
      query: query.slice(0, 128),
      search_source: 'submit'
    });
  }, true);

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('predictive-search a[href]');
    if (!link) return;
    var host = link.closest('predictive-search');
    var input = host && host.querySelector('input[name="q"]');
    var query = input && input.value ? input.value.trim() : '';
    recordTelemetry('hi_search', {
      query: query.slice(0, 128),
      search_source: 'predictive_pick',
      picked_path: String(link.getAttribute('href') || '').split('?')[0].slice(0, 256)
    });
  }, true);

  /* Collection filtering and sorting. Debounced because Dawn's facets
     fire one change per checkbox; the form snapshot (not the URL, which
     only updates after the fetch settles) is the source of truth. */
  var facetEmitTimer = null;
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.closest || !input.closest('facet-filters-form')) return;
    var formEl = input.form;
    clearTimeout(facetEmitTimer);
    facetEmitTimer = setTimeout(function () {
      var filters = '';
      try {
        if (formEl) filters = new URLSearchParams(new FormData(formEl)).toString();
      } catch (e) {}
      if (!filters) filters = window.location.search.replace(/^\?/, '');
      recordTelemetry('hi_collection_filter', {
        filters: filters.slice(0, 512),
        collection_path: window.location.pathname.slice(0, 256)
      });
    }, 600);
  }, true);

  /* Newsletter signups: Dawn's {% form 'customer' %} sections and the
     Klaviyo direct-subscribe forms. Recorded at submit (an attempt);
     both flows validate the email before this listener can fire from a
     button press. */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || form.nodeName !== 'FORM' || !form.querySelector) return;
    var isKlaviyo = form.hasAttribute('data-hn-klaviyo-form');
    var formType = form.querySelector('input[name="form_type"]');
    var isCustomerForm = Boolean(
      formType && formType.value === 'customer' && form.querySelector('input[name="contact[email]"]')
    );
    if (!isKlaviyo && !isCustomerForm) return;
    var source = form.getAttribute('data-source') || (isKlaviyo ? 'klaviyo' : 'newsletter');
    recordTelemetry('hi_newsletter_signup', {
      signup_source: String(source).slice(0, 64)
    });
  }, true);

  /* Variant consideration on the PDP, via Dawn's own pub/sub. Dedupe on
     the variant id so re-renders that republish the current variant do
     not stutter the journey. */
  var lastVariantSelectId = '';
  function trackVariantSelect(event) {
    var data = event && event.data;
    var variant = data && data.variant;
    if (!variant || !variant.id) return;
    var key = String(variant.id);
    if (key === lastVariantSelectId) return;
    lastVariantSelectId = key;
    recordTelemetry('hi_variant_select', {
      variant_id: key,
      variant_title: String(variant.title || '').slice(0, 128),
      price_cents: typeof variant.price === 'number' ? variant.price : 0,
      section_id: data.sectionId ? String(data.sectionId).slice(0, 64) : ''
    });
  }

  function subscribeToThemeEvents() {
    if (typeof window.subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return false;
    window.subscribe(PUB_SUB_EVENTS.cartUpdate, trackProductFormAdd);
    if (PUB_SUB_EVENTS.variantChange) {
      window.subscribe(PUB_SUB_EVENTS.variantChange, trackVariantSelect);
    }
    return true;
  }
  /* constants.js and pubsub.js load (deferred) ahead of this file, so the
     immediate call succeeds; the fallback covers any reordering. */
  if (!subscribeToThemeEvents()) {
    document.addEventListener('DOMContentLoaded', subscribeToThemeEvents);
  }

  window.HnAnalytics = {
    /* Single owner of the kill-switch predicate; hn-telemetry.js and any
       future sink consult this instead of duplicating the expression. */
    trackingDisabled: analyticsDisabled,
    currencyCode: currencyCode,
    toDollars: toDollars,
    pushEvent: pushEvent,
    pushEcommerce: pushEcommerce,
    markSessionFlag: markSessionFlag,
    getCheckoutFlags: getCheckoutFlags,
    setNextCartDrawerOpenReason: setNextCartDrawerOpenReason,
    trackCartDrawerView: trackCartDrawerView
  };
})();
